// Target Accounts Types

export interface TargetAccount {
  id: string
  name: string
  domain: string | null
  sector: string | null
  country: string | null
  city: string | null
  hubspot_owner_id: string | null
  employees: number | null
  revenue: number | null
  icp_tier: string | null
  volume_score: 'high' | 'medium' | 'low' | null
  international_score: 'high' | 'medium' | 'low' | null
  multiproduct_score: 'high' | 'medium' | 'low' | null
  icp_fit_score: 'excellent' | 'good' | 'medium' | 'low' | null
  overall_icp_score: 'excellent' | 'good' | 'medium' | 'low' | null
  // Additional fields
  industry: string | null
  lifecyclestage: string | null
  type: string | null
  description: string | null
  phone: string | null
  website: string | null
  linkedin_company_page: string | null
  num_associated_contacts: number | null
  num_associated_deals: number | null
  notes_last_contacted: string | null
  createdate: string | null
  hs_lastmodifieddate: string | null
  account_status: 'active' | 'engaged' | 'archived' | null
}

export interface Owner {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
}

export interface TargetAccountsResponse {
  accounts: TargetAccount[]
  owners: Owner[]
  total: number
}

// Column configuration
export interface ColumnConfig {
  id: string
  label: string
  field: keyof TargetAccount | 'owner'
  type: 'text' | 'number' | 'date' | 'score' | 'badge'
  sortable: boolean
  defaultVisible: boolean
  width?: string
  description?: string
  scoreExplanation?: ScoreExplanation // Detailed explanation for score columns
}

// Score explanation structure
export interface ScoreExplanation {
  title: string
  description: string
  calculation: string
  thresholds?: { label: string; condition: string }[]
}

// Score calculation explanations
export const SCORE_EXPLANATIONS: Record<string, ScoreExplanation> = {
  overall_icp_score: {
    title: 'Overall ICP Score',
    description: 'Composite score combining all ICP factors',
    calculation: 'Weighted combination of Volume (35%), International (25%), Multi-Product (20%), and Conversion (20%)',
    thresholds: [
      { label: 'Excellent', condition: '3+ factors at High' },
      { label: 'Good', condition: '2 High or 3+ Medium' },
      { label: 'Medium', condition: '1 High or 2 Medium' },
      { label: 'Low', condition: 'All other combinations' },
    ],
  },
  volume_score: {
    title: 'Volume Potential',
    description: 'Estimated transaction/verification volume based on company size and industry',
    calculation: 'Derived from employee count, revenue, and industry vertical patterns',
    thresholds: [
      { label: 'High', condition: '>1M potential verifications/year' },
      { label: 'Medium', condition: '100K-1M verifications/year' },
      { label: 'Low', condition: '<100K verifications/year' },
    ],
  },
  international_score: {
    title: 'International Reach',
    description: 'Global expansion potential and multi-region needs',
    calculation: 'Based on headquarters, operating countries, and subsidiaries',
    thresholds: [
      { label: 'High', condition: 'Operations in 3+ regions' },
      { label: 'Medium', condition: 'Operations in 2 regions' },
      { label: 'Low', condition: 'Single region operation' },
    ],
  },
  multiproduct_score: {
    title: 'Multi-Product Fit',
    description: 'Likelihood to need multiple FrankieOne products',
    calculation: 'Analysis of KYC, AML, Fraud, Biometrics, and KYB requirements',
    thresholds: [
      { label: 'High', condition: '4+ product fit' },
      { label: 'Medium', condition: '2-3 product fit' },
      { label: 'Low', condition: 'Single product fit' },
    ],
  },
  icp_fit_score: {
    title: 'Conversion Likelihood',
    description: 'Probability of converting to customer based on ICP alignment',
    calculation: 'Sector alignment, company size, tech maturity, buying signals, and competitive positioning',
    thresholds: [
      { label: 'Excellent', condition: 'Strong fit + active signals' },
      { label: 'Good', condition: 'Good fit + some signals' },
      { label: 'Medium', condition: 'Moderate fit' },
      { label: 'Low', condition: 'Poor fit or mismatch' },
    ],
  },
}

export const AVAILABLE_COLUMNS: ColumnConfig[] = [
  // Default visible columns
  { id: 'name', label: 'Company', field: 'name', type: 'text', sortable: true, defaultVisible: true, width: 'min-w-[250px]' },
  { id: 'owner', label: 'Owner', field: 'owner', type: 'text', sortable: true, defaultVisible: true, width: 'min-w-[120px]' },
  { id: 'overall_icp_score', label: 'Overall', field: 'overall_icp_score', type: 'score', sortable: true, defaultVisible: true, width: 'w-[90px]', description: 'Overall ICP Score', scoreExplanation: SCORE_EXPLANATIONS.overall_icp_score },
  { id: 'sector', label: 'Sector', field: 'sector', type: 'badge', sortable: true, defaultVisible: true, width: 'min-w-[140px]' },
  { id: 'account_status', label: 'Status', field: 'account_status', type: 'badge', sortable: true, defaultVisible: true, width: 'w-[100px]' },
  { id: 'volume_score', label: 'Vol', field: 'volume_score', type: 'score', sortable: true, defaultVisible: true, width: 'w-[70px]', description: 'Volume Potential', scoreExplanation: SCORE_EXPLANATIONS.volume_score },
  { id: 'international_score', label: 'Intl', field: 'international_score', type: 'score', sortable: true, defaultVisible: true, width: 'w-[70px]', description: 'International Reach', scoreExplanation: SCORE_EXPLANATIONS.international_score },
  { id: 'multiproduct_score', label: 'Multi', field: 'multiproduct_score', type: 'score', sortable: true, defaultVisible: true, width: 'w-[70px]', description: 'Multi-Product Fit', scoreExplanation: SCORE_EXPLANATIONS.multiproduct_score },
  { id: 'icp_fit_score', label: 'Conv', field: 'icp_fit_score', type: 'score', sortable: true, defaultVisible: true, width: 'w-[80px]', description: 'Conversion Likelihood', scoreExplanation: SCORE_EXPLANATIONS.icp_fit_score },

  // Additional columns (hidden by default)
  { id: 'country', label: 'Country', field: 'country', type: 'text', sortable: true, defaultVisible: false, width: 'min-w-[120px]' },
  { id: 'city', label: 'City', field: 'city', type: 'text', sortable: true, defaultVisible: false, width: 'min-w-[120px]' },
  { id: 'icp_tier', label: 'ICP Tier', field: 'icp_tier', type: 'badge', sortable: true, defaultVisible: false, width: 'w-[100px]' },
  { id: 'industry', label: 'Industry', field: 'industry', type: 'text', sortable: true, defaultVisible: false, width: 'min-w-[140px]' },
  { id: 'employees', label: 'Employees', field: 'employees', type: 'number', sortable: true, defaultVisible: false, width: 'w-[100px]' },
  { id: 'revenue', label: 'Revenue', field: 'revenue', type: 'number', sortable: true, defaultVisible: false, width: 'w-[120px]' },
  { id: 'num_associated_contacts', label: '# Contacts', field: 'num_associated_contacts', type: 'number', sortable: true, defaultVisible: false, width: 'w-[100px]' },
  { id: 'num_associated_deals', label: '# Deals', field: 'num_associated_deals', type: 'number', sortable: true, defaultVisible: false, width: 'w-[90px]' },
  { id: 'lifecyclestage', label: 'Lifecycle', field: 'lifecyclestage', type: 'badge', sortable: true, defaultVisible: false, width: 'w-[110px]' },
  { id: 'type', label: 'Type', field: 'type', type: 'badge', sortable: true, defaultVisible: false, width: 'w-[100px]' },
  { id: 'notes_last_contacted', label: 'Last Contacted', field: 'notes_last_contacted', type: 'date', sortable: true, defaultVisible: false, width: 'w-[130px]' },
  { id: 'createdate', label: 'Created', field: 'createdate', type: 'date', sortable: true, defaultVisible: false, width: 'w-[110px]' },
]

export type SortField = keyof TargetAccount | 'owner'

export type SortOrder = 'asc' | 'desc'

export interface TargetAccountsFilters {
  search: string
  owner: string
  sector: string
  overallScore: string
  status: string
}

export type AccountStatus = 'active' | 'engaged' | 'archived'

export const ACCOUNT_STATUS_OPTIONS: { value: AccountStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'engaged', label: 'Engaged' },
  { value: 'archived', label: 'Archived' },
]
